import { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { ContactShadows, Environment, Html, Lightformer, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import './ThreeDCanvas.css';

const CARD_DATA = [
  {
    title: 'Brand Hub',
    label: 'AI Creative',
    copy: 'Studio generation complete',
    metric: 'ROAS +22%',
    badge: 'High CTR',
    icon: 'image',
    kind: 'product',
    image: '/ChatGPT Image Jun 24, 2026, 11_38_23 AM.webp',
  },
  {
    title: 'AI Product Set',
    label: 'Studio Scene',
    copy: 'Commercial mockups ready',
    metric: 'CTR 5.8%',
    badge: 'Sale Live',
    icon: 'sparkles',
    kind: 'creative',
    image: '/ChatGPT Image Jun 24, 2026, 11_40_14 AM.webp',
  },
  {
    title: 'Ad Poster',
    label: 'Poster Design',
    copy: 'Visual assets generated',
    metric: '+41%',
    badge: 'Ready',
    icon: 'megaphone',
    kind: 'social',
    image: '/Gemini_Generated_Image_3zvzs83zvzs83zvz.webp',
  },
  {
    title: 'Product Shot',
    label: 'Creative Asset',
    copy: 'High-res background fit',
    metric: '1.2M reach',
    badge: 'AI Fit',
    icon: 'target',
    kind: 'product',
    image: '/Gemini_Generated_Image_ebi68gebi68gebi6 (3).webp',
  },
  {
    title: 'Creative Kit',
    label: 'Campaign Ready',
    copy: 'Multi-format asset set',
    metric: '86% match',
    badge: 'Active',
    icon: 'percent',
    kind: 'promo',
    image: '/Gemini_Generated_Image_ebi68gebi68gebi6 (4).webp',
  },
];

const ICON_TILES = [
  { icon: 'megaphone', position: [-3.78, 1.56, -0.36], rotation: [0.02, 0.32, -0.06], phase: 0.4, speed: 1.1 },
  { icon: 'bag', position: [3.82, 1.5, -0.34], rotation: [0.02, -0.34, 0.08], phase: 1.7, speed: 1.0 },
  { icon: 'cursor', position: [-3.48, -0.12, 0.62], rotation: [-0.06, 0.28, 0.05], phase: 2.5, speed: 1.2 },
  { icon: 'sparkles', position: [3.4, -0.1, 0.66], rotation: [-0.06, -0.26, -0.05], phase: 3.2, speed: 1.15 },
  { icon: 'social', position: [-0.82, 2.12, -0.82], rotation: [0.02, 0.12, -0.04], phase: 4.1, speed: 0.92, hideOnMobile: true },
  { icon: 'chart', position: [0.9, 2.12, -0.82], rotation: [0.02, -0.12, 0.04], phase: 5.2, speed: 0.95, hideOnMobile: true },
];

const PLATFORM_VIOLET = '#8172ec';
const PLATFORM_VIOLET_LIGHT = '#a99eff';
const PLATFORM_VIOLET_SOFT = '#d8d4ff';
const PLATFORM_VIOLET_DARK = '#6251d7';
const PLATFORM_VIOLET_DEEP = '#25145d';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();

    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }

    media.addListener(update);
    return () => media.removeListener(update);
  }, [query]);

  return matches;
}

function MarketingIcon({ type }) {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.8',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': 'true',
  };

  switch (type) {
    case 'chart':
      return (
        <svg {...commonProps}>
          <path d="M4 19V5" />
          <path d="M4 19h16" />
          <path d="M8 16v-4" />
          <path d="M12 16V8" />
          <path d="M16 16v-6" />
        </svg>
      );
    case 'target':
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="7" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 3v3" />
          <path d="M21 12h-3" />
          <path d="M12 21v-3" />
          <path d="M3 12h3" />
        </svg>
      );
    case 'image':
      return (
        <svg {...commonProps}>
          <rect x="4" y="5" width="16" height="14" rx="3" />
          <circle cx="9" cy="10" r="1.4" />
          <path d="m7 17 3.8-4 2.8 2.8 1.7-1.7L18 17" />
        </svg>
      );
    case 'percent':
      return (
        <svg {...commonProps}>
          <path d="M7 17 17 7" />
          <circle cx="7.5" cy="7.5" r="2" />
          <circle cx="16.5" cy="16.5" r="2" />
        </svg>
      );
    case 'cursor':
      return (
        <svg {...commonProps}>
          <path d="m6 4 12 8-5.2 1.1L10.5 19 6 4Z" />
          <path d="m12.8 13.1 3.1 4.4" />
        </svg>
      );
    case 'bag':
      return (
        <svg {...commonProps}>
          <path d="M7 9h10l1 10H6L7 9Z" />
          <path d="M9 9a3 3 0 0 1 6 0" />
        </svg>
      );
    case 'social':
      return (
        <svg {...commonProps}>
          <rect x="5" y="5" width="14" height="14" rx="4" />
          <path d="M8.5 10.2h7" />
          <path d="M8.5 13.2h4.8" />
          <circle cx="9" cy="16" r="0.8" />
          <circle cx="12" cy="16" r="0.8" />
          <circle cx="15" cy="16" r="0.8" />
        </svg>
      );
    case 'sparkles':
      return (
        <svg {...commonProps}>
          <path d="M12 3l1.7 5.1L19 10l-5.3 1.9L12 17l-1.7-5.1L5 10l5.3-1.9L12 3Z" />
          <path d="M19 15l.7 2.1L22 18l-2.3.9L19 21l-.7-2.1L16 18l2.3-.9L19 15Z" />
        </svg>
      );
    case 'megaphone':
    default:
      return (
        <svg {...commonProps}>
          <path d="M4 11v3h3l9 4V7l-9 4H4Z" />
          <path d="M7 14l1.1 4" />
          <path d="M18 9.5c.8.6 1.2 1.5 1.2 2.5s-.4 1.9-1.2 2.5" />
        </svg>
      );
  }
}

function CardVisual({ kind, image }) {
  if (image) {
    return (
      <div className="ad-card-visual ad-card-visual--image">
        <img src={image} alt="Ad Visual" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }

  if (kind === 'analytics') {
    return (
      <div className="ad-card-visual ad-card-visual--analytics">
        <span style={{ height: '44%' }} />
        <span style={{ height: '76%' }} />
        <span style={{ height: '58%' }} />
        <span style={{ height: '92%' }} />
      </div>
    );
  }

  if (kind === 'audience') {
    return (
      <div className="ad-card-visual ad-card-visual--audience">
        <span />
        <span />
        <span />
      </div>
    );
  }

  return (
    <div className={`ad-card-visual ad-card-visual--${kind}`}>
      <span className="ad-card-thumbnail" />
      <span className="ad-card-line ad-card-line--wide" />
      <span className="ad-card-line" />
    </div>
  );
}

function AdPreviewCard({ card }) {
  if (card.image) {
    return (
      <div className={`ad-preview-card ad-preview-card--only-image`}>
        <img
          src={card.image}
          alt={card.title}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit'
          }}
        />
      </div>
    );
  }

  return (
    <div className={`ad-preview-card ad-preview-card--${card.kind}`}>
      <div className="ad-card-head">
        <span className="ad-card-icon">
          <MarketingIcon type={card.icon} />
        </span>
        <span className="ad-card-title-wrap">
          <span className="ad-card-label">{card.label}</span>
          <strong>{card.title}</strong>
        </span>
      </div>

      <CardVisual kind={card.kind} image={card.image} />

      <div className="ad-card-footer">
        <span>{card.copy}</span>
        <strong>{card.metric}</strong>
      </div>

      <span className="ad-card-badge">{card.badge}</span>
    </div>
  );
}

function AiPlatform({ reducedMotion, isCompact }) {
  const ringRef = useRef(null);
  const dotsRef = useRef(null);

  const baseRadius = isCompact ? 2.3 : 3.2;
  const s = baseRadius / 2.05;

  useFrame((state, delta) => {
    if (reducedMotion) return;
    if (ringRef.current) ringRef.current.rotation.z += delta * 0.28;
    if (dotsRef.current) dotsRef.current.rotation.y -= delta * 0.16;
  });

  return (
    <group position={[0, -0.36, 0]}>
      {/* Ambient and point lights for neon glow effects */}
      <pointLight position={[0, -0.2, 0.35 * s]} color={PLATFORM_VIOLET_LIGHT} intensity={1.1} distance={4.6} decay={2} />
      {/* Central glow */}
      <pointLight position={[0, -0.09, 0]} color={PLATFORM_VIOLET} intensity={1.5} distance={3.0} decay={2} />

      {/* Base shadow glow */}
      <mesh position={[0, -0.46, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[2.05 * s, 96]} />
        <meshBasicMaterial color={PLATFORM_VIOLET} transparent opacity={0.22} depthWrite={false} toneMapped={false} />
      </mesh>

      {/* --- Tier 1 (Bottom) --- */}
      <mesh position={[0, -0.36, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[1.45 * s, 1.45 * s, 0.18, 96]} />
        <meshPhysicalMaterial color="#64748b" roughness={0.1} metalness={0.05} clearcoat={1} clearcoatRoughness={0.1} />
      </mesh>
      {/* Torus for rounded edge */}
      <mesh position={[0, -0.27, 0]} rotation={[Math.PI / 2, 0, 0]}>
         <torusGeometry args={[1.45 * s - 0.02, 0.02, 32, 96]} />
         <meshPhysicalMaterial color="#64748b" roughness={0.1} metalness={0.05} />
      </mesh>
      {/* Border Ring */}
      <mesh position={[0, -0.27, 0]} rotation={[Math.PI / 2, 0, 0]}>
         <torusGeometry args={[1.45 * s, 0.005 * s, 16, 96]} />
         <meshStandardMaterial color={PLATFORM_VIOLET_SOFT} roughness={0.5} metalness={0.5} />
      </mesh>

      {/* Tier 1 Detail Line */}
      <mesh position={[0, -0.27, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.32 * s, 0.005 * s, 8, 96]} />
        <meshStandardMaterial color={PLATFORM_VIOLET_SOFT} roughness={0.8} />
      </mesh>

      {/* Tier 1 Edge Indicator LED Lights */}
      {Array.from({ length: 8 }).map((_, index) => {
        const angle = (index / 8) * Math.PI * 2;
        return (
          <group key={index} position={[Math.cos(angle) * 1.32 * s, -0.26, Math.sin(angle) * 1.32 * s]}>
            <mesh>
              <sphereGeometry args={[0.02 * s, 16, 16]} />
              <meshBasicMaterial color={PLATFORM_VIOLET_SOFT} />
            </mesh>
            <pointLight color={PLATFORM_VIOLET_SOFT} intensity={0.5} distance={0.5} decay={2} />
          </group>
        );
      })}

      {/* --- Tier 2 (Middle Platform) --- */}
      <mesh position={[0, -0.15, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[1.2 * s, 1.2 * s, 0.12, 96]} />
        <meshPhysicalMaterial color={PLATFORM_VIOLET_LIGHT} roughness={0.15} metalness={0.04} clearcoat={0.9} clearcoatRoughness={0.1} />
      </mesh>
      {/* Rounded edge for Tier 2 */}
      <mesh position={[0, -0.09, 0]} rotation={[Math.PI / 2, 0, 0]}>
         <torusGeometry args={[1.2 * s - 0.02, 0.02, 32, 96]} />
         <meshPhysicalMaterial color={PLATFORM_VIOLET_LIGHT} roughness={0.15} metalness={0.04} />
      </mesh>
      {/* Border Ring */}
      <mesh position={[0, -0.09, 0]} rotation={[Math.PI / 2, 0, 0]}>
         <torusGeometry args={[1.2 * s, 0.005 * s, 16, 96]} />
         <meshStandardMaterial color={PLATFORM_VIOLET_SOFT} roughness={0.5} metalness={0.5} />
      </mesh>

      {/* Glowing Neon Gap (between Tier 2 and top tiers) */}
      <mesh position={[0, -0.03, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.85 * s, 0.85 * s, 0.1, 96]} />
        <meshStandardMaterial color={PLATFORM_VIOLET_DEEP} roughness={0.8} />
      </mesh>
      
      {/* 8 Bright Violet Point Lights in the Gap */}
      {Array.from({ length: 8 }).map((_, index) => {
        const angle = (index / 8) * Math.PI * 2;
        return (
          <group key={`glow-${index}`} position={[Math.cos(angle) * 0.9 * s, -0.03, Math.sin(angle) * 0.9 * s]}>
            <mesh>
              <sphereGeometry args={[0.03 * s, 16, 16]} />
              <meshBasicMaterial color={PLATFORM_VIOLET_SOFT} />
            </mesh>
            <pointLight color={PLATFORM_VIOLET} intensity={2.0} distance={1.2} decay={2} />
          </group>
        );
      })}

      {/* --- Tier 3 (Slotted Ring) --- */}
      <mesh position={[0, 0.08, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[1.05 * s, 1.05 * s, 0.08, 96]} />
        <meshPhysicalMaterial color="#ebe8ff" roughness={0.1} metalness={0.02} clearcoat={1.0} clearcoatRoughness={0.05} />
      </mesh>
      <mesh position={[0, 0.12, 0]} rotation={[Math.PI / 2, 0, 0]}>
         <torusGeometry args={[1.05 * s - 0.02, 0.02, 32, 96]} />
         <meshPhysicalMaterial color="#ebe8ff" roughness={0.1} metalness={0.02} />
      </mesh>
      {/* Border Ring */}
      <mesh position={[0, 0.12, 0]} rotation={[Math.PI / 2, 0, 0]}>
         <torusGeometry args={[1.05 * s, 0.005 * s, 16, 96]} />
         <meshStandardMaterial color={PLATFORM_VIOLET_SOFT} roughness={0.5} metalness={0.5} />
      </mesh>

      {/* The 8 Rectangular Slots on Tier 3 */}
      {Array.from({ length: 8 }).map((_, index) => {
        const angle = (index / 8) * Math.PI * 2 + (Math.PI / 8);
        return (
          <mesh 
            key={`slot-${index}`} 
            position={[Math.cos(angle) * 0.96 * s, 0.119, Math.sin(angle) * 0.96 * s]}
            rotation={[0, -angle, 0]}
          >
            <boxGeometry args={[0.06 * s, 0.02, 0.1 * s]} />
            <meshStandardMaterial color={PLATFORM_VIOLET_DARK} roughness={0.9} />
          </mesh>
        );
      })}

      {/* --- Tier 4 (Center Pad) --- */}
      <mesh position={[0, 0.10, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.75 * s, 0.75 * s, 0.1, 96]} />
        <meshPhysicalMaterial color="#f2efff" roughness={0.1} metalness={0.02} clearcoat={1.0} clearcoatRoughness={0.05} />
      </mesh>
      <mesh position={[0, 0.15, 0]} rotation={[Math.PI / 2, 0, 0]}>
         <torusGeometry args={[0.75 * s - 0.02, 0.02, 32, 96]} />
         <meshPhysicalMaterial color="#f2efff" roughness={0.1} metalness={0.02} />
      </mesh>
      {/* Border Ring */}
      <mesh position={[0, 0.15, 0]} rotation={[Math.PI / 2, 0, 0]}>
         <torusGeometry args={[0.75 * s, 0.005 * s, 16, 96]} />
         <meshStandardMaterial color={PLATFORM_VIOLET_SOFT} roughness={0.5} metalness={0.5} />
      </mesh>

      <mesh position={[0, 0.15, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.62 * s, 0.005 * s, 8, 96]} />
        <meshStandardMaterial color={PLATFORM_VIOLET_SOFT} roughness={0.8} />
      </mesh>

      {/* Orbiting Violet Beads on the Wire */}
      <group ref={dotsRef} position={[0, 0.1, 0]}>
        {Array.from({ length: 5 }).map((_, idx) => {
          const angle = (idx / 5) * Math.PI * 2;
          return (
            <group key={`orbit-${idx}`} position={[Math.cos(angle) * 1.75 * s, 0, Math.sin(angle) * 1.75 * s]}>
              <mesh castShadow>
                <sphereGeometry args={[0.05 * s, 16, 16]} />
                <meshStandardMaterial color={PLATFORM_VIOLET} emissive={PLATFORM_VIOLET} emissiveIntensity={1.5} roughness={0.1} />
              </mesh>
              <pointLight color={PLATFORM_VIOLET} intensity={0.5} distance={1} decay={2} />
            </group>
          );
        })}
      </group>

      {/* Static Orbit Wire Ring */}
      <mesh position={[0, 0.1, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.75 * s, 0.004 * s, 8, 96]} />
        <meshBasicMaterial color={PLATFORM_VIOLET_SOFT} transparent opacity={0.6} />
      </mesh>

      {/* Dynamic Spinning Accent Ring on Top Platform */}
      <group ref={ringRef} position={[0, 0.155, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <mesh>
          <torusGeometry args={[0.55 * s, 0.006 * s, 8, 96]} />
          <meshBasicMaterial color={PLATFORM_VIOLET_SOFT} transparent opacity={0.65} />
        </mesh>
      </group>

      {/* Local geometric "AI" mark avoids font/SDF generation during the first frame. */}
      <group position={[0, 0.21, 0]} scale={s * 0.9}>
        {[
          { key: 'a-left', position: [-0.31, 0, 0], rotation: -0.2, size: [0.12, 0.09, 0.7] },
          { key: 'a-right', position: [-0.07, 0, 0], rotation: 0.2, size: [0.12, 0.09, 0.7] },
          { key: 'a-bar', position: [-0.19, 0, 0.06], rotation: 0, size: [0.31, 0.09, 0.1] },
          { key: 'i-stem', position: [0.31, 0, 0], rotation: 0, size: [0.12, 0.09, 0.7] },
          { key: 'i-top', position: [0.31, 0, -0.3], rotation: 0, size: [0.34, 0.09, 0.1] },
          { key: 'i-bottom', position: [0.31, 0, 0.3], rotation: 0, size: [0.34, 0.09, 0.1] },
        ].map((piece) => (
          <mesh
            key={piece.key}
            position={piece.position}
            rotation={[0, piece.rotation, 0]}
            castShadow
          >
            <boxGeometry args={piece.size} />
            <meshStandardMaterial
              color={PLATFORM_VIOLET_LIGHT}
              emissive={PLATFORM_VIOLET_SOFT}
              emissiveIntensity={0.55}
              roughness={0.2}
              metalness={0.1}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
}

function OrbitingCard({ card, index, cardsCount, isCompact, reducedMotion }) {
  const group = useRef(null);
  const initialized = useRef(false);
  const targetPosition = useMemo(() => new THREE.Vector3(), []);
  const targetScale = useMemo(() => new THREE.Vector3(1, 1, 1), []);

  useFrame((state, delta) => {
    if (!group.current) return;

    const t = reducedMotion ? 0 : state.clock.elapsedTime;
    const interval = 4; // 4 seconds per step
    const transitionDuration = 1.0; // 1 second transition
    
    const currentSlot = Math.floor(t / interval);
    const progress = t % interval;
    
    const easeInOutCubic = (x) => {
      return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    };
    
    const activeIndex = currentSlot + (progress < (interval - transitionDuration) ? 0 : easeInOutCubic((progress - (interval - transitionDuration)) / transitionDuration));
    const angle = (index + activeIndex) * (Math.PI * 2 / cardsCount);
    
    const radius = isCompact ? 2.9 : 4.4;
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    const y = 0.18 + Math.sin(angle) * 0.04;

    const scaleFactor = THREE.MathUtils.mapLinear(z, -radius, radius, 0.80, 1.10);
    const bob = reducedMotion ? 0 : Math.sin(t * 1.12 + index * 0.82) * 0.06;

    targetPosition.set(x, y + bob, z);
    targetScale.setScalar(scaleFactor);

    const damp = initialized.current ? 1 - Math.exp(-delta * 7.8) : 1;
    group.current.position.lerp(targetPosition, damp);
    group.current.scale.lerp(targetScale, damp);

    const normX = x / radius;
    const normZ = z / radius;
    const rotX = -normZ * 0.16;
    const rotY = -normX * 0.31;
    const rotZ = normX * 0.03;

    group.current.rotation.x = THREE.MathUtils.lerp(group.current.rotation.x, rotX, damp);
    group.current.rotation.y = THREE.MathUtils.lerp(group.current.rotation.y, rotY, damp);
    group.current.rotation.z = THREE.MathUtils.lerp(group.current.rotation.z, rotZ, damp);

    initialized.current = true;
  });

  return (
    <group ref={group}>
      <RoundedBox args={[1.58, 0.92, 0.055]} radius={0.07} smoothness={5} position={[0, 0, -0.03]} castShadow>
        <meshPhysicalMaterial color="#ffffff" roughness={0.82} metalness={0.02} transparent opacity={0.72} />
      </RoundedBox>
      <Html transform center distanceFactor={4.35} zIndexRange={[80, 0]}>
        <AdPreviewCard card={card} />
      </Html>
    </group>
  );
}

function FloatingIconTile({ tile, reducedMotion, scaleFactor }) {
  const group = useRef(null);

  useFrame((state) => {
    if (!group.current) return;
    const t = reducedMotion ? 0 : state.clock.elapsedTime;
    group.current.position.y = tile.position[1] + Math.sin(t * tile.speed + tile.phase) * 0.07;
    group.current.rotation.z = tile.rotation[2] + Math.sin(t * 0.48 + tile.phase) * 0.018;
  });

  const scaledPosition = [
    tile.position[0] * scaleFactor,
    tile.position[1],
    tile.position[2] * scaleFactor
  ];

  return (
    <group ref={group} position={scaledPosition} rotation={tile.rotation}>
      <Html transform center distanceFactor={4.85} zIndexRange={[60, 0]}>
        <div className="marketing-icon-tile">
          <MarketingIcon type={tile.icon} />
        </div>
      </Html>
    </group>
  );
}

function DottedWave({ reducedMotion, isCompact }) {
  const pointsRef = useRef(null);
  const latticeRef = useRef(null);

  const wave = useMemo(() => {
    const cols = isCompact ? 130 : 260;
    const rows = isCompact ? 30 : 42;
    const positions = new Float32Array(cols * rows * 3);
    const colors = new Float32Array(cols * rows * 3);
    const latticePositions = [];
    const latticeColors = [];
    const edgeColor = new THREE.Color('#64748b');
    const midColor = new THREE.Color('#475569');
    const coreColor = new THREE.Color('#334155');
    const latticeColor = new THREE.Color('#708097');
    const mixed = new THREE.Color();
    const rowStep = isCompact ? 5 : 6;
    const colStep = isCompact ? 14 : 18;
    let i = 0;

    for (let c = 0; c < cols; c += 1) {
      const xNorm = c / (cols - 1);
      const x = THREE.MathUtils.mapLinear(xNorm, 0, 1, isCompact ? -11.0 : -17.0, isCompact ? 11.0 : 17.0);
      const horizontalFade = Math.sin(xNorm * Math.PI);

      for (let r = 0; r < rows; r += 1) {
        const laneNorm = r / (rows - 1);
        const lane = THREE.MathUtils.mapLinear(laneNorm, 0, 1, -1, 1);
        const rowFade = Math.max(0, 1 - Math.abs(lane));
        const colorStrength = Math.pow((0.3 + horizontalFade * 0.7) * (0.28 + rowFade * 0.72), 0.8);

        positions[i] = x;
        positions[i + 1] = 0;
        positions[i + 2] = lane;

        mixed.copy(edgeColor).lerp(midColor, colorStrength * 0.82).lerp(coreColor, colorStrength * 0.24);
        colors[i] = mixed.r;
        colors[i + 1] = mixed.g;
        colors[i + 2] = mixed.b;
        i += 3;

        if (r % rowStep === 0 || c % colStep === 0) {
          latticePositions.push(x, 0, lane);
          latticeColors.push(latticeColor.r, latticeColor.g, latticeColor.b);
        }
      }
    }

    const lattice = new Float32Array(latticePositions);
    return {
      positions,
      base: positions.slice(),
      colors,
      lattice,
      latticeBase: lattice.slice(),
      latticeColors: new Float32Array(latticeColors),
    };
  }, [isCompact]);

  useFrame((state) => {
    if (!pointsRef.current) return;
    const t = reducedMotion ? 0 : state.clock.elapsedTime;
    const span = isCompact ? 11.0 : 17.0;
    const amplitude = isCompact ? 1.4 : 2.2;
    const ribbonLift = isCompact ? 0.6 : 0.95;
    const ribbonDepth = isCompact ? 1.0 : 1.5;
    const baseDepth = isCompact ? -2.05 : -2.32;

    const updatePositions = (targetRef, base) => {
      if (!targetRef.current) return;
      const positionAttribute = targetRef.current.geometry.getAttribute('position');
      const arr = positionAttribute.array;

      for (let i = 0; i < arr.length; i += 3) {
        const x = base[i];
        const lane = base[i + 2];
        const edgeFade = 0.72 + Math.max(0, 1 - Math.abs(x) / span) * 0.28;
        const phase = x * 0.94 + t * 0.22;
        const secondaryPhase = x * 1.58 + t * 0.15;
        const centerLine = (
          Math.sin(phase) * amplitude +
          Math.sin(secondaryPhase) * amplitude * 0.22
        ) * edgeFade;
        const twist = phase + lane * 0.42;
        const pinch = 0.62 + Math.pow(Math.abs(Math.cos(phase)), 1.35) * 0.58;
        const surfaceLift = lane * Math.cos(twist) * ribbonLift * pinch;
        const breathing = Math.sin(x * 0.34 + t * 0.22) * 0.05;

        arr[i + 1] = centerLine + surfaceLift + breathing;
        arr[i + 2] = baseDepth + lane * Math.sin(twist) * ribbonDepth * pinch;
      }

      positionAttribute.needsUpdate = true;
    };

    updatePositions(pointsRef, wave.base);
    updatePositions(latticeRef, wave.latticeBase);
  });

  return (
    <group key={isCompact ? 'compact-wave' : 'desktop-wave'} position={[0, -0.45, 0]} rotation={[0.02, 0, 0]} frustumCulled={false}>
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            array={wave.positions}
            count={wave.positions.length / 3}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            array={wave.colors}
            count={wave.colors.length / 3}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          vertexColors
          size={isCompact ? 0.029 : 0.033}
          sizeAttenuation
          transparent
          opacity={0.58}
          depthWrite={false}
        />
      </points>
      <points ref={latticeRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            array={wave.lattice}
            count={wave.lattice.length / 3}
            itemSize={3}
          />
          <bufferAttribute
            attach="attributes-color"
            array={wave.latticeColors}
            count={wave.latticeColors.length / 3}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          vertexColors
          size={isCompact ? 0.044 : 0.052}
          sizeAttenuation
          transparent
          opacity={0.74}
          depthWrite={false}
        />
      </points>
    </group>
  );
}

function CameraRig({ pointer, reducedMotion, isCompact }) {
  useFrame((state, delta) => {
    const damp = 1 - Math.exp(-delta * 3.6);
    const camera = state.camera;
    const targetX = reducedMotion ? 0 : pointer.current.x * 0.34;
    const targetY = (isCompact ? 2.6 : 2.4) + (reducedMotion ? 0 : pointer.current.y * 0.16);
    const targetZ = isCompact ? 12.5 : 11.5;

    camera.position.x += (targetX - camera.position.x) * damp;
    camera.position.y += (targetY - camera.position.y) * damp;
    camera.position.z += (targetZ - camera.position.z) * damp;
    camera.lookAt(0, isCompact ? 0.32 : 0.38, 0);
  });

  return null;
}

function HeroScene({ isCompact, reducedMotion }) {
  const cards = useMemo(
    () => (isCompact ? CARD_DATA.filter((card) => !card.hideOnMobile) : CARD_DATA),
    [isCompact]
  );
  const iconTiles = useMemo(
    () => (isCompact ? ICON_TILES.filter((tile) => !tile.hideOnMobile) : ICON_TILES),
    [isCompact]
  );

  const baseRadius = isCompact ? 2.3 : 3.2;
  const s = baseRadius / 2.05;

  return (
    <group position={[0, isCompact ? 0.70 : 0.90, 0]}>
      <DottedWave reducedMotion={reducedMotion} isCompact={isCompact} />
      <group rotation={[Math.PI * 25 / 180, 0, 0]}>
        <AiPlatform reducedMotion={reducedMotion} isCompact={isCompact} />
        {cards.map((card, index) => (
          <OrbitingCard
            key={card.title}
            card={card}
            index={index}
            cardsCount={cards.length}
            isCompact={isCompact}
            reducedMotion={reducedMotion}
          />
        ))}
        {iconTiles.map((tile) => (
          <FloatingIconTile key={`${tile.icon}-${tile.phase}`} tile={tile} reducedMotion={reducedMotion} scaleFactor={s} />
        ))}
      </group>
    </group>
  );
}

export default function ThreeDCanvas({ active = true }) {
  const isCompact = useMediaQuery('(max-width: 700px)');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const scenePaused = reducedMotion || !active;
  const pointer = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (scenePaused) return undefined;

    const onMove = (event) => {
      pointer.current.x = event.clientX / window.innerWidth - 0.5;
      pointer.current.y = -(event.clientY / window.innerHeight - 0.5);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [scenePaused]);

  return (
    <div className={`threed-canvas ${isCompact ? 'is-compact' : ''}`} aria-hidden="true">
      <Canvas
        dpr={[1, 1.25]}
        frameloop={scenePaused ? 'demand' : 'always'}
        shadows
        gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
        performance={{ min: 0.5 }}
        camera={{ position: [0, isCompact ? 2.6 : 2.4, isCompact ? 12.5 : 11.5], fov: isCompact ? 50 : 40 }}
      >
        <Environment resolution={64}>
          <Lightformer
            form="rect"
            intensity={1.55}
            color="#ffffff"
            position={[0, 5, -4]}
            rotation={[Math.PI / 2, 0, 0]}
            scale={[8, 8, 1]}
          />
          <Lightformer
            form="rect"
            intensity={1.1}
            color="#d8d4ff"
            position={[-5, 2, 1]}
            rotation={[0, Math.PI / 2, 0]}
            scale={[7, 4, 1]}
          />
          <Lightformer
            form="rect"
            intensity={0.8}
            color="#ffffff"
            position={[5, 1, 3]}
            rotation={[0, -Math.PI / 2, 0]}
            scale={[6, 3, 1]}
          />
        </Environment>
        <ambientLight intensity={0.52} />
        <hemisphereLight args={['#ffffff', '#ebe8ff', 0.72]} />
        <directionalLight
          position={[-4.8, 6.4, 4.4]}
          intensity={1.75}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-bias={-0.0005}
        />
        <directionalLight position={[5.8, 3.2, 4.8]} intensity={0.74} />
        <pointLight position={[0, 1.7, 3.2]} color="#ffffff" intensity={0.78} distance={8} />

        <HeroScene isCompact={isCompact} reducedMotion={scenePaused} />

        <ContactShadows
          position={[0, isCompact ? 0.07 : 0.27, 0]}
          opacity={0.18}
          scale={11.0}
          blur={4.8}
          far={5}
          frames={1}
          resolution={512}
          color="#8e96a3"
        />

        <CameraRig pointer={pointer} reducedMotion={scenePaused} isCompact={isCompact} />
      </Canvas>
    </div>
  );
}
